import { _, FreezerService, IAjaxState, managedAjaxUtil } from 'Imports';
import { AgreementsService } from '$State/AgreementsFreezerService';
import { ConfigService } from '$State/ConfigFreezerService';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { PricingPlanService } from '$State/PricingPlanFreezerService';
import { UserInformationResponseResponseBase, UserApiFactory, UserInformationResponse } from '$Generated/api';

const InjectedPropName = 'userManagement';

export interface IUserManagementState {
    hasError: boolean;
    userCredential: string;
    isUserDataLoaded: boolean;
    isCredentialRenewalActive: boolean;
    successfulGeotabGroupRetrieval?: boolean;
    getCurrentUserInfoResults: IAjaxState<UserInformationResponseResponseBase>;
}

export interface IntegrationUserCredential {
    xIntegrationUserCredential: string;
}

class UserManagementFreezerService extends FreezerService<IUserManagementState, typeof InjectedPropName> {
    constructor() {
        super(
            {
                hasError: false,
                userCredential: '',
                isUserDataLoaded: false,
                isCredentialRenewalActive: false,
                getCurrentUserInfoResults: managedAjaxUtil.createInitialState(),
            },
            InjectedPropName,
        );
    }

    public getUserCredential() {
        return this.freezer.get().userCredential;
    }

    public setUserCredential(value: string) {
        this.freezer.get().set({ userCredential: value });
    }

    public isLoggedIn() {
        return !_.isEmpty(this.getUserCredential());
    }

    public hasValidSession(): boolean {
        const credential = this.getUserCredential();
        if (_.isEmpty(credential)) {
            return false;
        }

        try {
            const parsed = JSON.parse(credential);
            return !_.isEmpty(parsed?.SessionId);
        } catch {
            return false;
        }
    }

    public generateIntegrationUserParams(): IntegrationUserCredential {
        const credential: any = this.isLoggedIn() ? this.getUserCredential() : '';
        return { xIntegrationUserCredential: credential };
    }

    public getIsUserDataLoaded(): boolean {
        return this.freezer.get().isUserDataLoaded;
    }

    public setIsUserDataLoaded(value: boolean) {
        this.freezer.get().set({ isUserDataLoaded: value });
    }

    public getIsCredentialRenewalActive(): boolean {
        return this.freezer.get().isCredentialRenewalActive;
    }

    public setIsCredentialRenewalActive(isActive: boolean) {
        this.freezer.get().set({ isCredentialRenewalActive: isActive });
    }

    public async setupUserData() {
        if (!this.isLoggedIn()) {
            console.error('UserManagementFreezer.setupUserData(): Called before user credential is available');
            // The component (RequiredAuth) will keep showing loading state until credential arrives
            return;
        }

        this.setIsUserDataLoaded(false);

        try {
            if (AgreementsService.getShowAgreements() && AgreementsService.getState().signedAgreementsResults.data == null) {
                await AgreementsService.getSignedAgreements();
            }

            await ConfigService.getFleetId();
            if (ConfigService.isFleetIdValid()) {
                await IntegrationPartnerDataService.getUserRoles();
                await IntegrationPartnerDataService.getUser();
                await IntegrationPartnerDataService.getDrivers();
                await IntegrationPartnerDataService.getDevices();
                await PricingPlanService.getPricingPlan();
            }
        } catch (e) {
            console.error('UserManagementFreezer.setupUserData(): ' + e);
        }
        this.setIsUserDataLoaded(true);
    }

    public async tryLogin(): Promise<boolean> {
        await IntegrationPartnerDataService.getIntegrationPartnerManager().handleCredentialExpiration(); // Check to ensure Authentication is still valid

        if (!this.isLoggedIn()) {
            const result = await IntegrationPartnerDataService.getIntegrationPartnerManager().tryLogin();
            return typeof result === 'boolean' ? result : this.isLoggedIn();
        }
        return true;
    }

    public getRoles() {
        return IntegrationPartnerDataService.getUserRolesState();
    }

    public logout() {
        this.freezer.get().set({ userCredential: '' });
        this.setIsUserDataLoaded(false);
        IntegrationPartnerDataService.getIntegrationPartnerManager().logout();
    }

    public getCurrentUserInfo(): Promise<void | UserInformationResponseResponseBase> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'getCurrentUserInfoResults',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (!params) {
                    throw new Error('Parameters is undefined');
                }
                const UserApi = UserApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return UserApi.apiV1UsersCurrentGet(params);
            },
            params: {
                ...this.generateIntegrationUserParams(),
                ...{},
            },
            onOk: (data: UserInformationResponseResponseBase) => {
                if (data !== undefined) {
                    this.freezer.get().set({ hasError: false });
                }
                return data;
            },
            onError: (error) => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public getCurrentUserId(): string | null {
        const currentUserInfo = this.freezer.get().getCurrentUserInfoResults?.data?.data ?? null;
        return currentUserInfo?.userId ?? null;
    }

    public getCurrentFleetName(): string {
        const currentUserInfo = this.freezer.get().getCurrentUserInfoResults?.data?.data ?? null;
        return currentUserInfo?.currentFleet?.name ?? '';
    }
}

export const UserManagementService = new UserManagementFreezerService();
export type IUserManagementServiceInjectedProps = ReturnType<UserManagementFreezerService['getPropsForInjection']>;
